import React, { useState, useEffect, useRef } from 'react';
import contentService from '../services/contentService';

import Loading from './Loading';
/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-CL'));
const today = () =>
  new Date().toLocaleDateString('es-CL', { month: 'long', year: 'numeric' }).toUpperCase();

const engRate = (interactions, posts, followers) => {
  if (!followers || !posts) return null;
  return ((interactions / posts / followers) * 100).toFixed(2);
};

/* ─────────────────────────────────────────────────────────────
   Sub-componentes reutilizables (UI + PDF)
───────────────────────────────────────────────────────────── */
const KpiCard = ({ icon, label, value, color = '#1d6c38', small, forPdf }) => (
  <div style={{
    background: '#fff',
    borderRadius: forPdf ? '8px' : '10px',
    padding: forPdf ? '12px 10px' : (small ? '14px 12px' : '18px 16px'),
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    border: '1px solid #f0f0f0',
  }}>
    <div style={{ fontSize: forPdf ? '18px' : (small ? '20px' : '24px') }}>{icon}</div>
    <div style={{ fontSize: forPdf ? '18px' : (small ? '20px' : '26px'), fontWeight: 800, color, lineHeight: 1.1, textAlign: 'center' }}>
      {value}
    </div>
    <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {label}
    </div>
  </div>
);

const SectionHeader = ({ icon, title, subtitle, bgColor, textColor = '#fff', forPdf }) => (
  <div style={{
    background: bgColor, borderRadius: '10px', padding: forPdf ? '10px 16px' : '12px 20px',
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: forPdf ? '12px' : '14px',
  }}>
    <div style={{ fontSize: '20px' }}>{icon}</div>
    <div>
      <div style={{ fontWeight: 800, fontSize: '14px', color: textColor }}>{title}</div>
      {subtitle && <div style={{ fontSize: '11px', color: textColor, opacity: 0.8 }}>{subtitle}</div>}
    </div>
  </div>
);

const PostsTable = ({ posts, platform, likeField = 'like_count', commentField = 'comment_count', extraFields = [], forPdf }) => {
  const sorted = [...(posts || [])]
    .sort((a, b) => ((b[likeField] || 0) + (b[commentField] || 0)) - ((a[likeField] || 0) + (a[commentField] || 0)))
    .slice(0, forPdf ? 5 : 5);

  if (!sorted.length) return (
    <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>Sin datos</p>
  );

  const thStyle = { padding: '8px 8px', fontWeight: 700, color: '#374151', fontSize: '11px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' };
  const tdStyle = { padding: '7px 8px', fontSize: '12px', borderBottom: '1px solid #f3f4f6' };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead>
        <tr>
          <th style={{ ...thStyle, textAlign: 'left', width: '28px' }}>#</th>
          {!forPdf && <th style={{ ...thStyle, width: '48px' }}>Img</th>}
          <th style={{ ...thStyle, textAlign: 'left' }}>Contenido</th>
          <th style={{ ...thStyle, textAlign: 'center', width: '70px' }}>Likes</th>
          <th style={{ ...thStyle, textAlign: 'center', width: '75px' }}>Coment.</th>
          {extraFields.map(f => (
            <th key={f.key} style={{ ...thStyle, textAlign: 'center', width: '70px' }}>{f.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((p, idx) => {
          const thumb = p.thumbnail_url || p.media_url;
          const caption = (p.caption || '').slice(0, 80);
          return (
            <tr key={p.id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ ...tdStyle, color: '#9ca3af', fontWeight: 700 }}>{idx + 1}</td>
              {!forPdf && (
                <td style={{ ...tdStyle, padding: '5px' }}>
                  {thumb ? (
                    <img src={thumb} alt="" crossOrigin="anonymous"
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '5px', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={{ width: '40px', height: '40px', background: '#f3f4f6', borderRadius: '5px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      {platform === 'tiktok' ? 'TK' : platform === 'facebook' ? 'FB' : 'IG'}
                    </div>
                  )}
                </td>
              )}
              <td style={{ ...tdStyle, maxWidth: forPdf ? '280px' : '200px', color: '#374151' }}>
                {caption || <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Sin texto</span>}
                {p.caption?.length > 80 ? '…' : ''}
              </td>
              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{fmt(p[likeField])}</td>
              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#3b82f6' }}>{fmt(p[commentField])}</td>
              {extraFields.map(f => (
                <td key={f.key} style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: f.color || '#374151' }}>
                  {fmt(p[f.key])}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/* ─────────────────────────────────────────────────────────────
   Sección de plataforma (reutilizada en tab y PDF)
───────────────────────────────────────────────────────────── */
const IGSection = ({ posts, followers, username, loading, error, forPdf }) => {
  const likes = posts.reduce((s, p) => s + (p.like_count || 0), 0);
  const comments = posts.reduce((s, p) => s + (p.comment_count || p.comments_count || 0), 0);
  const interactions = likes + comments;
  const er = engRate(interactions, posts.length, followers);
  const totalViews = posts.reduce((s, p) => s + (p.view_count || 0), 0);
  const reelPosts = posts.filter(p => (p.view_count || 0) > 0);
  const gridCols = forPdf ? 'repeat(4, 1fr)' : 'repeat(auto-fill, minmax(110px, 1fr))';
  return (
    <div>
      <SectionHeader icon="📸" title="Instagram · Indicadores"
        subtitle={`@${username} · ${posts.length} posts analizados`}
        bgColor="linear-gradient(135deg, #e1306c, #fd1d1d, #fcb045)" forPdf={forPdf} />
      {loading && <Loading fullScreen={false} size="sm" />}
      {error && <p style={{ color: '#ef4444', fontSize: '13px', padding: '8px 0' }}>{error}</p>}
      {!loading && posts.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', marginBottom: '14px' }}>
            {followers && <KpiCard label="Seguidores" value={fmt(followers)} color="#e1306c" small forPdf={forPdf} />}
            <KpiCard label="Total likes" value={fmt(likes)} color="#ef4444" small forPdf={forPdf} />
            <KpiCard label="Comentarios" value={fmt(comments)} color="#3b82f6" small forPdf={forPdf} />
            <KpiCard label="Interacciones" value={fmt(interactions)} color="#8b5cf6" small forPdf={forPdf} />
            <KpiCard label="Posts" value={fmt(posts.length)} color="#374151" small forPdf={forPdf} />
            <KpiCard label="Avg likes/post" value={posts.length ? fmt(Math.round(likes / posts.length)) : '—'} color="#e1306c" small forPdf={forPdf} />
            <KpiCard label="Avg coment./post" value={posts.length ? fmt(Math.round(comments / posts.length)) : '—'} color="#3b82f6" small forPdf={forPdf} />
            {followers && <KpiCard label="Eng. Rate" value={er ? `${er}%` : '—'} color="#10b981" small forPdf={forPdf} />}
            <KpiCard label="Total views (Reels)" value={fmt(totalViews)} color="#f59e0b" small forPdf={forPdf} />
            {reelPosts.length > 0 && (
              <KpiCard label="Avg views/Reel" value={fmt(Math.round(totalViews / reelPosts.length))} color="#f59e0b" small forPdf={forPdf} />
            )}
          </div>
          <PostsTable
            posts={posts} platform="instagram" likeField="like_count" commentField="comment_count"
            extraFields={[{ key: 'view_count', label: 'Views', color: '#f59e0b' }]}
            forPdf={forPdf} />
        </>
      )}
      {!loading && !error && posts.length === 0 && (
        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '12px' }}>Sin publicaciones disponibles.</p>
      )}
    </div>
  );
};

const TKSection = ({ videos, username, profile, loading, error, forPdf }) => {
  const likes = videos.reduce((s, v) => s + (v.like_count || 0), 0);
  const comments = videos.reduce((s, v) => s + (v.comment_count || 0), 0);
  const plays = videos.reduce((s, v) => s + (v.play_count || 0), 0);
  const gridCols = forPdf ? 'repeat(4, 1fr)' : 'repeat(auto-fill, minmax(110px, 1fr))';
  return (
    <div>
      <SectionHeader icon="🎬" title="TikTok · Indicadores"
        subtitle={`@${username} · ${videos.length} videos analizados`}
        bgColor="linear-gradient(135deg, #010101, #e91e8c)" forPdf={forPdf} />
      {loading && <Loading fullScreen={false} size="sm" />}
      {error && <p style={{ color: '#ef4444', fontSize: '13px', padding: '8px 0' }}>{error}</p>}
      {!loading && videos.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', marginBottom: '14px' }}>
            {profile?.followers && (
              <KpiCard label="Seguidores" value={fmt(profile.followers)} color="#e91e8c" small forPdf={forPdf} />
            )}
            <KpiCard label="Total likes" value={fmt(likes)} color="#ef4444" small forPdf={forPdf} />
            <KpiCard label="Comentarios" value={fmt(comments)} color="#3b82f6" small forPdf={forPdf} />
            <KpiCard label="Total views" value={fmt(plays)} color="#10b981" small forPdf={forPdf} />
            <KpiCard label="Videos analizados" value={fmt(videos.length)} color="#374151" small forPdf={forPdf} />
            {profile?.video_count && (
              <KpiCard label="Videos totales" value={fmt(profile.video_count)} color="#6b7280" small forPdf={forPdf} />
            )}
            <KpiCard label="Avg likes/video" value={videos.length ? fmt(Math.round(likes / videos.length)) : '—'} color="#ef4444" small forPdf={forPdf} />
            <KpiCard label="Avg views/video" value={videos.length ? fmt(Math.round(plays / videos.length)) : '—'} color="#10b981" small forPdf={forPdf} />
          </div>
          <PostsTable posts={videos} platform="tiktok" likeField="like_count" commentField="comment_count"
            extraFields={[{ key: 'play_count', label: 'Views', color: '#10b981' }]} forPdf={forPdf} />
        </>
      )}
      {!loading && !error && videos.length === 0 && (
        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '12px' }}>Sin videos disponibles.</p>
      )}
    </div>
  );
};

const FBSection = ({ posts, username, profile, loading, error, forPdf }) => {
  const likes = posts.reduce((s, p) => s + (p.like_count || 0), 0);
  const comments = posts.reduce((s, p) => s + (p.comment_count || 0), 0);
  const interactions = likes + comments;
  const gridCols = forPdf ? 'repeat(4, 1fr)' : 'repeat(auto-fill, minmax(110px, 1fr))';
  return (
    <div>
      <SectionHeader icon="📘" title="Facebook · Indicadores"
        subtitle={`@${username} · ${posts.length} posts analizados`}
        bgColor="#1877f2" forPdf={forPdf} />
      {loading && <Loading fullScreen={false} size="sm" />}
      {error && <p style={{ color: '#ef4444', fontSize: '13px', padding: '8px 0' }}>{error}</p>}
      {!loading && posts.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', marginBottom: '14px' }}>
            {profile?.followers && (
              <KpiCard label="Seguidores" value={fmt(profile.followers)} color="#1877f2" small forPdf={forPdf} />
            )}
            {profile?.likes && (
              <KpiCard label="Likes página" value={fmt(profile.likes)} color="#0a5bc4" small forPdf={forPdf} />
            )}
            <KpiCard label="Reacciones" value={fmt(likes)} color="#1877f2" small forPdf={forPdf} />
            <KpiCard label="Comentarios" value={fmt(comments)} color="#3b82f6" small forPdf={forPdf} />
            <KpiCard label="Interacciones" value={fmt(interactions)} color="#0a5bc4" small forPdf={forPdf} />
            <KpiCard label="Posts analizados" value={fmt(posts.length)} color="#374151" small forPdf={forPdf} />
            <KpiCard label="Avg reacc./post" value={posts.length ? fmt(Math.round(likes / posts.length)) : '—'} color="#1877f2" small forPdf={forPdf} />
            <KpiCard label="Avg coment./post" value={posts.length ? fmt(Math.round(comments / posts.length)) : '—'} color="#3b82f6" small forPdf={forPdf} />
          </div>
          <PostsTable posts={posts} platform="facebook" likeField="like_count" commentField="comment_count" forPdf={forPdf} />
        </>
      )}
      {!loading && !error && posts.length === 0 && (
        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '12px' }}>Sin publicaciones disponibles.</p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Modal principal
───────────────────────────────────────────────────────────── */
const InformeCompetidorModal = ({ competidor, postsLimit = 20, onClose }) => {
  const [igPosts, setIgPosts] = useState([]);
  const [tkVideos, setTkVideos] = useState([]);
  const [fbPosts, setFbPosts] = useState([]);
  const [igFollowers, setIgFollowers] = useState(null);
  const [tkProfile, setTkProfile] = useState(null);
  const [fbProfile, setFbProfile] = useState(null);

  const [loadingIG, setLoadingIG] = useState(false);
  const [loadingTK, setLoadingTK] = useState(false);
  const [loadingFB, setLoadingFB] = useState(false);

  const [errorIG, setErrorIG] = useState(null);
  const [errorTK, setErrorTK] = useState(null);
  const [errorFB, setErrorFB] = useState(null);

  const [activeTab, setActiveTab] = useState('resumen');

  // Filtro de fechas
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Función para filtrar posts por rango de fechas
  const filterByDate = (posts) => {
    if (!dateFrom && !dateTo) return posts;
    return posts.filter(p => {
      const ts = p.timestamp || p.date || '';
      if (!ts) return true;
      const postDate = ts.slice(0, 10); // YYYY-MM-DD
      if (dateFrom && postDate < dateFrom) return false;
      if (dateTo   && postDate > dateTo)   return false;
      return true;
    });
  };

  // Estado del snapshot de seguidores
  const [snapshotToday, setSnapshotToday] = useState(null);  // null = cargando, false = no existe, objeto = ya guardado
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const pdfRef = useRef(null);

  const anyLoading = loadingIG || loadingTK || loadingFB;

  /* ── Carga de datos ── */
  useEffect(() => {
    if (competidor.ig_username) fetchIG();
    if (competidor.tk_username) { fetchTK(); fetchTKProfile(); }
    if (competidor.fb_username) { fetchFB(); fetchFBProfile(); }
    fetchSnapshotToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSnapshotToday = async () => {
    try {
      const res = await contentService.getFollowerSnapshotToday(competidor.id);
      setSnapshotToday(res.data.exists ? res.data : false);
    } catch {
      setSnapshotToday(false);
    }
  };

  const handleSaveSnapshot = async () => {
    setSavingSnapshot(true);
    setSnapshotMsg(null);
    try {
      const res = await contentService.saveFollowerSnapshot(competidor.id, {
        ig_followers: igFollowers || null,
        tk_followers: tkProfile?.followers || null,
        fb_followers: fbProfile?.followers || null,
        fb_likes:     fbProfile?.likes || null,
      });
      setSnapshotToday(res.data);
      setSnapshotMsg({ type: 'ok', text: res.data.created ? '✓ Guardado correctamente' : '✓ Actualizado (ya existía una muestra hoy)' });
    } catch {
      setSnapshotMsg({ type: 'err', text: '✗ Error al guardar' });
    } finally {
      setSavingSnapshot(false);
    }
  };

  const fetchIG = async () => {
    setLoadingIG(true); setErrorIG(null);

    // Primario: EnsembleData (scraping, sin costo Meta)
    try {
      const res = await contentService.getEnsembleDataIGPosts(competidor.ig_username, postsLimit);
      const posts = res.data?.posts || [];
      if (posts.length > 0) {
        setIgPosts(posts);
        if (res.data?.followers) setIgFollowers(res.data.followers);
        setLoadingIG(false);
        return;
      }
    } catch {
      // silencioso — intentar fallback
    }

    // Fallback: Instagram Business Discovery API (Meta, gratuito)
    try {
      const res2 = await contentService.getIGBusinessDiscovery({
        username: competidor.ig_username,
        include_media: '1',
        media_limit: postsLimit,
      });
      const media = res2.data?.media?.data || [];
      if (media.length > 0) {
        setIgPosts(media.map(p => ({
          id:            p.id || '',
          caption:       p.caption || '',
          thumbnail_url: p.media_url || '',
          permalink:     p.permalink || '',
          timestamp:     p.timestamp || '',
          like_count:    p.like_count || 0,
          comment_count: p.comments_count || 0,
          view_count:    0,
          media_type:    p.media_type || 'IMAGE',
        })));
        if (res2.data?.followers_count) setIgFollowers(res2.data.followers_count);
      } else {
        setErrorIG('No se pudieron cargar los posts de Instagram.');
      }
    } catch {
      setErrorIG('No se pudieron cargar los posts de Instagram.');
    } finally {
      setLoadingIG(false);
    }
  };

  const fetchTKProfile = async () => {
    try {
      const res = await contentService.getSociaVaultTikTokProfile(competidor.tk_username);
      if (res.data?.followers) setTkProfile(res.data);
    } catch {
      // silencioso — el perfil es complementario, no bloquea el informe
    }
  };

  const fetchTK = async () => {
    setLoadingTK(true); setErrorTK(null);
    try {
      const res = await contentService.getEnsembleDataTikTokPosts(competidor.tk_username, postsLimit);
      const vids = res.data?.videos || [];
      if (vids.length > 0) { setTkVideos(vids); return; }
      throw new Error('sin resultados ED');
    } catch {
      try {
        const res2 = await contentService.getTikTokCompetitorPosts(competidor.tk_username, postsLimit);
        setTkVideos(res2.data?.videos || []);
      } catch { setErrorTK('No se pudieron cargar los videos de TikTok.'); }
    } finally { setLoadingTK(false); }
  };

  const fetchFB = async () => {
    setLoadingFB(true); setErrorFB(null);
    try {
      const res = await contentService.getApifyFacebookPosts(competidor.fb_username, postsLimit);
      setFbPosts(res.data?.posts || []);
    } catch { setErrorFB('No se pudieron cargar los posts de Facebook.'); }
    finally { setLoadingFB(false); }
  };

  const fetchFBProfile = async () => {
    // Primario: Meta Graph API (gratis, sin créditos)
    try {
      const res = await contentService.getMetaFacebookProfile(competidor.fb_username);
      if (res.data?.followers) { setFbProfile(res.data); return; }
    } catch {
      // silencioso — intentar fallback
    }
    // Fallback: SociaVault (requiere créditos)
    try {
      const res2 = await contentService.getSociaVaultFacebookProfile(competidor.fb_username);
      if (res2.data?.followers) setFbProfile(res2.data);
    } catch {
      // silencioso — el perfil es complementario, no bloquea el informe
    }
  };

  /* ── Generación de PDF ── */
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  const handleDownloadPDF = async () => {
    setIsPdfGenerating(true);
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await new Promise(r => setTimeout(r, 150));

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const PAGE_W = 210; // mm
      const PAGE_H = 297; // mm
      let curY = 0;
      let firstSection = true;

      // Capturar un canvas de una sección y cortarlo en trozos si supera la página
      const addSectionToPdf = async (el) => {
        const canvas = await window.html2canvas(el, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          width: el.offsetWidth,
          height: el.scrollHeight,
          windowWidth: el.offsetWidth,
        });

        const imgW = PAGE_W;
        const imgH = (canvas.height * PAGE_W) / canvas.width; // alto total en mm

        // Si la sección no cabe en el espacio restante y no estamos al tope → nueva página
        if (!firstSection && curY + imgH > PAGE_H) {
          pdf.addPage();
          curY = 0;
        }
        firstSection = false;

        // Si la sección cabe entera en el espacio disponible → colocar de una vez
        if (curY + imgH <= PAGE_H) {
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, curY, imgW, imgH);
          curY += imgH;
          return;
        }

        // La sección es más alta que el espacio de una página → trocear con canvas slicing
        const pxPerMm = canvas.width / PAGE_W; // píxeles por mm
        let sliceOffsetMm = 0;

        while (sliceOffsetMm < imgH) {
          const availMm  = PAGE_H - curY;
          const sliceMm  = Math.min(imgH - sliceOffsetMm, availMm);
          const srcY     = Math.round(sliceOffsetMm * pxPerMm);
          const srcH     = Math.round(sliceMm * pxPerMm);

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width  = canvas.width;
          sliceCanvas.height = srcH;
          sliceCanvas.getContext('2d').drawImage(
            canvas, 0, srcY, canvas.width, srcH,
            0, 0, canvas.width, srcH
          );

          pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, curY, imgW, sliceMm);
          sliceOffsetMm += sliceMm;
          curY          += sliceMm;

          if (sliceOffsetMm < imgH) {
            pdf.addPage();
            curY = 0;
          }
        }
      };

      // Procesar cada sección marcada con data-pdf-section
      const sections = Array.from(pdfRef.current.querySelectorAll('[data-pdf-section]'));
      for (const section of sections) {
        await addSectionToPdf(section);
      }

      const filename = `informe-${competidor.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 7)}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF. Intenta nuevamente.');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  /* ── Arrays filtrados por fecha ── */
  const filteredIG = filterByDate(igPosts);
  const filteredTK = filterByDate(tkVideos);
  const filteredFB = filterByDate(fbPosts);

  /* ── Métricas calculadas ── */
  const igLikes = filteredIG.reduce((s, p) => s + (p.like_count || 0), 0);
  const igComments = filteredIG.reduce((s, p) => s + (p.comments_count || 0), 0);
  const igInteractions = igLikes + igComments;

  const tkLikes = filteredTK.reduce((s, v) => s + (v.like_count || 0), 0);
  const tkComments = filteredTK.reduce((s, v) => s + (v.comment_count || 0), 0);
  const tkInteractions = tkLikes + tkComments;

  const fbLikes = filteredFB.reduce((s, p) => s + (p.like_count || 0), 0);
  const fbComments = filteredFB.reduce((s, p) => s + (p.comment_count || 0), 0);
  const fbInteractions = fbLikes + fbComments;

  const totalInteractions = igInteractions + tkInteractions + fbInteractions;
  const totalPosts = filteredIG.length + filteredTK.length + filteredFB.length;

  const TABS = [
    { key: 'resumen', label: 'Resumen' },
    competidor.ig_username && { key: 'instagram', label: '📸 Instagram' },
    competidor.tk_username && { key: 'tiktok', label: '🎬 TikTok' },
    competidor.fb_username && { key: 'facebook', label: '📘 Facebook' },
    { key: 'top', label: 'Top Posts' },
  ].filter(Boolean);

  const LoadingChip = ({ name }) => (
    <Loading fullScreen={false} size="sm" mensaje={`Cargando ${name}`} />
  );

  /* ─── Estilos compartidos ─── */
  const kpiGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px', marginBottom: '14px' };

  return (
    <>
      <style>{`
        .informe-tab { padding: 8px 16px; border: none; background: transparent; cursor: pointer;
          font-size: 13px; font-weight: 600; color: #6b7280; border-bottom: 3px solid transparent;
          transition: all 0.15s; white-space: nowrap; }
        .informe-tab:hover { color: #1d6c38; }
        .informe-tab.active { color: #1d6c38; border-bottom-color: #1d6c38; }
        .pdf-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 16px;
          border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
          border: none; transition: all 0.15s; }
        .pdf-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .pdf-btn:not(:disabled):hover { filter: brightness(1.1); }
      `}</style>

      {/* ── PDF content — off-screen, visible para html2canvas ── */}
      <div
        ref={pdfRef}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '800px',
          background: '#fff',
          fontFamily: 'Arial, Helvetica, sans-serif',
          zIndex: -1,
        }}
      >
        {/* Portada */}
        <div data-pdf-section="portada" style={{
          background: 'linear-gradient(135deg, #0f4c23 0%, #1d6c38 60%, #2d8a4e 100%)',
          padding: '28px 32px 22px',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
            Informe de Competencia · Dashboard Redes Sociales
          </div>
          <h1 style={{ margin: '0 0 6px', fontSize: '26px', fontWeight: 900, color: '#fff' }}>
            {competidor.name}
          </h1>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {competidor.ig_username && (
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                📸 @{competidor.ig_username}
              </span>
            )}
            {competidor.tk_username && (
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                🎬 @{competidor.tk_username}
              </span>
            )}
            {competidor.fb_username && (
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                📘 @{competidor.fb_username}
              </span>
            )}
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginLeft: 'auto' }}>
              {today()}
            </span>
          </div>
        </div>

        {/* KPIs Globales */}
        <div data-pdf-section="kpis" style={{ padding: '20px 24px 16px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 800, color: '#0f4c23', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            KPIs Globales
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
            <KpiCard label="Total interacciones" value={fmt(totalInteractions)} color="#0f4c23" forPdf />
            <KpiCard label="Publicaciones" value={fmt(totalPosts)} color="#1d6c38" forPdf />
            {competidor.ig_username && <KpiCard icon="📸" label="Posts IG" value={fmt(filteredIG.length)} color="#e1306c" forPdf />}
            {competidor.tk_username && <KpiCard icon="🎬" label="Videos TK" value={fmt(filteredTK.length)} color="#010101" forPdf />}
            {competidor.fb_username && <KpiCard icon="📘" label="Posts FB" value={fmt(filteredFB.length)} color="#1877f2" forPdf />}
          </div>

          {/* Barras de interacción */}
          {totalInteractions > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
              {competidor.ig_username && igInteractions > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', width: '80px' }}>📸 Instagram</span>
                  <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(igInteractions / totalInteractions * 100).toFixed(1)}%`,
                      background: 'linear-gradient(90deg, #e1306c, #fd1d1d)', borderRadius: '99px' }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#e1306c', width: '60px', textAlign: 'right' }}>
                    {fmt(igInteractions)}
                  </span>
                </div>
              )}
              {competidor.tk_username && tkInteractions > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', width: '80px' }}>🎬 TikTok</span>
                  <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(tkInteractions / totalInteractions * 100).toFixed(1)}%`,
                      background: 'linear-gradient(90deg, #010101, #e91e8c)', borderRadius: '99px' }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#010101', width: '60px', textAlign: 'right' }}>
                    {fmt(tkInteractions)}
                  </span>
                </div>
              )}
              {competidor.fb_username && fbInteractions > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', width: '80px' }}>📘 Facebook</span>
                  <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(fbInteractions / totalInteractions * 100).toFixed(1)}%`,
                      background: 'linear-gradient(90deg, #1877f2, #0a5bc4)', borderRadius: '99px' }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#1877f2', width: '60px', textAlign: 'right' }}>
                    {fmt(fbInteractions)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Instagram section */}
        {competidor.ig_username && (
          <div data-pdf-section="instagram" style={{ padding: '0 24px 20px' }}>
            <IGSection posts={filteredIG} followers={igFollowers} username={competidor.ig_username}
              loading={loadingIG} error={errorIG} forPdf />
          </div>
        )}

        {/* TikTok section */}
        {competidor.tk_username && (
          <div data-pdf-section="tiktok" style={{ padding: '0 24px 20px' }}>
            <TKSection videos={filteredTK} username={competidor.tk_username}
              profile={tkProfile} loading={loadingTK} error={errorTK} forPdf />
          </div>
        )}

        {/* Facebook section */}
        {competidor.fb_username && (
          <div data-pdf-section="facebook" style={{ padding: '0 24px 20px' }}>
            <FBSection posts={filteredFB} username={competidor.fb_username}
              profile={fbProfile} loading={loadingFB} error={errorFB} forPdf />
          </div>
        )}

        {/* Footer */}
        <div data-pdf-section="footer" style={{ padding: '12px 24px', background: '#f9fafb', borderTop: '1px solid #e5e7eb',
          fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
          Informe generado automáticamente por Geves · {new Date().toLocaleString('es-CL')}
        </div>
      </div>

      {/* ── Modal visible ── */}
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}
        onClick={onClose}
      >
        <div
          style={{ background: '#f4f7f4', borderRadius: '16px', width: '100%', maxWidth: '860px',
            maxHeight: '92vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 12px 60px rgba(0,0,0,0.30)', overflow: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ── Portada / Header ── */}
          <div style={{
            background: 'linear-gradient(135deg, #0f4c23 0%, #1d6c38 60%, #2d8a4e 100%)',
            padding: '20px 24px 16px', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  {(competidor.profile_image_url_saved || competidor.profile_image_url) && (
                    <img src={competidor.profile_image_url_saved || competidor.profile_image_url}
                      alt={competidor.name}
                      style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover',
                        border: '2px solid rgba(255,255,255,0.4)' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.08em' }}>Informe de Competencia · Top {postsLimit} posts</div>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#fff' }}>{competidor.name}</h2>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                  {competidor.ig_username && (
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                      📸 @{competidor.ig_username}
                    </span>
                  )}
                  {competidor.tk_username && (
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                      🎬 @{competidor.tk_username}
                    </span>
                  )}
                  {competidor.fb_username && (
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '2px 10px' }}>
                      📘 @{competidor.fb_username}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>{today()}</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Botón Seguidores */}
                  {(() => {
                    const alreadySaved = snapshotToday && snapshotToday.exists !== false;
                    const hasAnyFollower = igFollowers || tkProfile?.followers || fbProfile?.followers;
                    const disabled = anyLoading || savingSnapshot || !hasAnyFollower;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <button
                          onClick={handleSaveSnapshot}
                          disabled={disabled}
                          title={alreadySaved ? 'Ya guardado hoy — click para actualizar' : 'Guardar conteo de seguidores de hoy'}
                          style={{
                            background: alreadySaved ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.15)',
                            border: `1px solid ${alreadySaved ? '#10b981' : 'rgba(255,255,255,0.3)'}`,
                            color: disabled ? 'rgba(255,255,255,0.4)' : '#fff',
                            borderRadius: '8px', padding: '6px 14px', cursor: disabled ? 'default' : 'pointer',
                            fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                          }}
                        >
                          {savingSnapshot ? 'Guardando...' : alreadySaved ? '✓ Seguidores' : 'Seguidores'}
                        </button>
                        {snapshotMsg && (
                          <span style={{ fontSize: '10px', color: snapshotMsg.type === 'ok' ? '#6ee7b7' : '#fca5a5', whiteSpace: 'nowrap' }}>
                            {snapshotMsg.text}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {/* Botón PDF */}
                  <button
                    className="pdf-btn"
                    onClick={handleDownloadPDF}
                    disabled={anyLoading || isPdfGenerating}
                    title={anyLoading ? 'Espera a que terminen de cargar los datos' : 'Descargar informe en PDF'}
                    style={{
                      background: anyLoading || isPdfGenerating ? '#6b7280' : '#fff',
                      color: anyLoading || isPdfGenerating ? 'rgba(255,255,255,0.6)' : '#0f4c23',
                    }}
                  >
                    {isPdfGenerating ? <>Generando...</> : <>📄 PDF</>}
                  </button>
                  <button
                    onClick={onClose}
                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                      color: '#fff', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600 }}
                  >
                    ✕ Cerrar
                  </button>
                </div>
                {/* Indicador de carga */}
                {anyLoading && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {loadingIG && competidor.ig_username && <LoadingChip name="Instagram" />}
                    {loadingTK && competidor.tk_username && <LoadingChip name="TikTok" />}
                    {loadingFB && competidor.fb_username && <LoadingChip name="Facebook" />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Tabs + Filtro de fechas ── */}
          <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', overflowX: 'auto', flexShrink: 0, paddingLeft: '8px' }}>
            {TABS.map(t => (
              <button key={t.key} className={`informe-tab${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}>{t.label}</button>
            ))}
            {/* Filtro de fechas */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
              padding: '4px 12px', flexShrink: 0 }}>
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>Período:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                style={{ fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px',
                  padding: '3px 6px', color: '#374151', background: '#f9fafb', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                style={{ fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px',
                  padding: '3px 6px', color: '#374151', background: '#f9fafb', cursor: 'pointer' }}
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  title="Limpiar filtro"
                  style={{ fontSize: '11px', color: '#9ca3af', background: 'none', border: 'none',
                    cursor: 'pointer', padding: '2px 4px', borderRadius: '4px' }}
                >✕</button>
              )}
            </div>
          </div>

          {/* ── Contenido de tabs ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>

            {/* Resumen */}
            {activeTab === 'resumen' && (
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 800, color: '#0f4c23' }}>KPIs Globales</h3>
                <div style={{ ...kpiGridStyle, marginBottom: '24px' }}>
                  <KpiCard label="Total interacciones" value={fmt(totalInteractions)} color="#0f4c23" />
                  <KpiCard label="Publicaciones" value={fmt(totalPosts)} color="#1d6c38" />
                  {competidor.ig_username && <KpiCard icon="📸" label="Posts IG" value={fmt(filteredIG.length)} color="#e1306c" />}
                  {competidor.tk_username && <KpiCard icon="🎬" label="Videos TK" value={fmt(filteredTK.length)} color="#010101" />}
                  {competidor.fb_username && <KpiCard icon="📘" label="Posts FB" value={fmt(filteredFB.length)} color="#1877f2" />}
                </div>
                <h3 style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 800, color: '#374151' }}>Interacciones por Plataforma</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {competidor.ig_username && (
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 18px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ fontSize: '24px' }}>📸</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#374151', fontSize: '13px', marginBottom: '4px' }}>
                          Instagram {loadingIG && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                          {errorIG && <span style={{ color: '#ef4444', fontWeight: 400, fontSize: '12px' }}> {errorIG}</span>}
                        </div>
                        <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: totalInteractions > 0 ? `${(igInteractions / totalInteractions * 100).toFixed(1)}%` : '0%',
                            background: 'linear-gradient(90deg, #e1306c, #fd1d1d)', borderRadius: '99px', transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        <div style={{ fontWeight: 800, fontSize: '16px', color: '#e1306c' }}>{fmt(igInteractions)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{totalInteractions > 0 ? `${(igInteractions / totalInteractions * 100).toFixed(0)}%` : '0%'}</div>
                      </div>
                    </div>
                  )}
                  {competidor.tk_username && (
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 18px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ fontSize: '24px' }}>🎬</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#374151', fontSize: '13px', marginBottom: '4px' }}>
                          TikTok {loadingTK && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                          {errorTK && <span style={{ color: '#ef4444', fontWeight: 400, fontSize: '12px' }}> {errorTK}</span>}
                        </div>
                        <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: totalInteractions > 0 ? `${(tkInteractions / totalInteractions * 100).toFixed(1)}%` : '0%',
                            background: 'linear-gradient(90deg, #010101, #e91e8c)', borderRadius: '99px', transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        <div style={{ fontWeight: 800, fontSize: '16px', color: '#010101' }}>{fmt(tkInteractions)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{totalInteractions > 0 ? `${(tkInteractions / totalInteractions * 100).toFixed(0)}%` : '0%'}</div>
                      </div>
                    </div>
                  )}
                  {competidor.fb_username && (
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '14px 18px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ fontSize: '24px' }}>📘</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#374151', fontSize: '13px', marginBottom: '4px' }}>
                          Facebook {loadingFB && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                          {errorFB && <span style={{ color: '#ef4444', fontWeight: 400, fontSize: '12px' }}> {errorFB}</span>}
                        </div>
                        <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: totalInteractions > 0 ? `${(fbInteractions / totalInteractions * 100).toFixed(1)}%` : '0%',
                            background: 'linear-gradient(90deg, #1877f2, #0a5bc4)', borderRadius: '99px', transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        <div style={{ fontWeight: 800, fontSize: '16px', color: '#1877f2' }}>{fmt(fbInteractions)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{totalInteractions > 0 ? `${(fbInteractions / totalInteractions * 100).toFixed(0)}%` : '0%'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Instagram */}
            {activeTab === 'instagram' && (
              <IGSection posts={filteredIG} followers={igFollowers} username={competidor.ig_username}
                loading={loadingIG} error={errorIG} />
            )}

            {/* TikTok */}
            {activeTab === 'tiktok' && (
              <TKSection videos={filteredTK} username={competidor.tk_username}
                profile={tkProfile} loading={loadingTK} error={errorTK} />
            )}

            {/* Facebook */}
            {activeTab === 'facebook' && (
              <FBSection posts={filteredFB} username={competidor.fb_username}
                profile={fbProfile} loading={loadingFB} error={errorFB} />
            )}

            {/* Top Posts */}
            {activeTab === 'top' && (
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 800, color: '#0f4c23' }}>Top Posts por Plataforma</h3>
                {competidor.ig_username && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#e1306c' }}>
                      📸 Top Instagram {loadingIG && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                    </h4>
                    <PostsTable posts={filteredIG} platform="instagram" likeField="like_count" commentField="comment_count" />
                  </div>
                )}
                {competidor.tk_username && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#010101' }}>
                      🎬 Top TikTok {loadingTK && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                    </h4>
                    <PostsTable posts={filteredTK} platform="tiktok" likeField="like_count" commentField="comment_count"
                      extraFields={[{ key: 'play_count', label: 'Views', color: '#10b981' }]} />
                  </div>
                )}
                {competidor.fb_username && (
                  <div>
                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#1877f2' }}>
                      📘 Top Facebook {loadingFB && <span style={{ color: '#9ca3af', fontWeight: 400 }}>cargando...</span>}
                    </h4>
                    <PostsTable posts={filteredFB} platform="facebook" likeField="like_count" commentField="comment_count" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InformeCompetidorModal;
